/**
 * EvolutionIntro — the Phase 2 → 3 cinematic that opens a fresh Cambrian run.
 *
 * Mirrors MulticellStage's opener (single cell → colony): here the player's
 * actual colony (cell counts from the Phase 2 carryover) breathes at the
 * centre of a dimmed screen, then its cells migrate onto the outline of
 * the Haikouichthys body and fuse into it. The fish wakes, a title card
 * lands, and a pulsing "▸ CONTINUE" waits for a click. Any click or key
 * before the end skips to the end; one more completes.
 *
 * Screen-space only: the caller adds `container` to a screen-fixed layer,
 * freezes the world while `active`, and calls update(dt) every frame.
 */

import { Container, Graphics, Text } from 'pixi.js';
import { messageScale } from '@/ui/messageScale';

export type IntroCellType = 'core' | 'armor' | 'motor' | 'sensor';

export interface EvolutionIntroOpts {
  width: number;
  height: number;
  /** Colony make-up, in the order the cells were grown. */
  cells: IntroCellType[];
  /** Draws the fish body into `g` at radius `r` for time `t` (body-local, +x forward). */
  drawFish: (g: Graphics, r: number, t: number) => void;
  /** Draws the soft halo behind the fish. */
  drawHalo: (g: Graphics, r: number, t: number) => void;
  /** Called when the player continues past the end. */
  onComplete: () => void;
}

const FISH_R = 64;
const MAX_T = 9.0;

const CELL_COLOR: Record<IntroCellType, number> = {
  core: 0x4fd1c5, armor: 0xa78bfa, motor: 0x76e4f7, sensor: 0xfadf7c,
};

/** L2's ring layout: 4 in the centre ring, then 8, 12, 16, 20. */
const RING_SIZES = [4, 8, 12, 16, 20];

function ss(a: number, b: number, x: number): number {
  if (b <= a) return x >= b ? 1 : 0;
  const u = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return u * u * (3 - 2 * u);
}
function mixc(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  return (Math.round(ar + (br - ar) * t) << 16) | (Math.round(ag + (bg - ag) * t) << 8) | Math.round(ab + (bb - ab) * t);
}

interface IntroCell {
  type: IntroCellType;
  g: Graphics;
  /** Ring position (colony) and target on the fish outline (body-local, scaled). */
  rx: number; ry: number;
  tx: number; ty: number;
  phase: number;
  /** Stagger for the migration, 0..1. */
  order: number;
}

export class EvolutionIntro {
  readonly container = new Container();
  active = true;
  private t = 0;
  private ready = false;
  private holdT = 0;
  private done = false;
  private readonly opts: EvolutionIntroOpts;
  private scrim: Graphics;
  private glow: Graphics;
  private envelope: Graphics;
  private cells: IntroCell[] = [];
  private fishHalo: Graphics;
  private fish: Graphics;
  private flash: Graphics;
  private labelTop: Text;
  private title: Text;
  private subtitle: Text;
  private prompt: Text;
  private cellR: number;

  constructor(opts: EvolutionIntroOpts) {
    this.opts = opts;
    const { width: sw, height: sh } = opts;
    const cx = sw / 2, cy = sh / 2;

    this.scrim = new Graphics().rect(0, 0, sw, sh).fill({ color: 0x010308, alpha: 1 });
    this.scrim.alpha = 0;
    this.container.addChild(this.scrim);

    // Everything but the scrim sits in a content box that shrinks on short
    // screens: the colony-to-fish column plus title and prompt is taller
    // than a landscape phone.
    const content = new Container();
    const k = messageScale(sh);
    content.pivot.set(cx, cy);
    content.position.set(cx, cy);
    content.scale.set(k);
    this.container.addChild(content);

    // Colony envelope + a warm glow that grows as the cells fuse.
    this.envelope = new Graphics();
    this.envelope.position.set(cx, cy);
    content.addChild(this.envelope);
    this.glow = new Graphics();
    this.glow.position.set(cx, cy);
    content.addChild(this.glow);

    // Fish (hidden until the fusion). Halo under body.
    this.fishHalo = new Graphics();
    this.fishHalo.position.set(cx, cy);
    this.fishHalo.alpha = 0;
    content.addChild(this.fishHalo);
    this.fish = new Graphics();
    this.fish.position.set(cx, cy);
    this.fish.alpha = 0;
    content.addChild(this.fish);

    // Cells: the colony that finishes Level 2 is always the full 26-cell
    // cluster (4 spawn cells + 22 growth thresholds), so show that filled
    // circle — typed cells from the carryover first, core cells padding
    // the rest. Targets spread along the fish outline so the migration
    // reads as "the colony becomes the body".
    const FULL_COLONY = 26;
    const types: IntroCellType[] = opts.cells.slice(0, FULL_COLONY);
    while (types.length < FULL_COLONY) types.push('core');
    const n = FULL_COLONY;
    this.cellR = 13;
    const ringGap = this.cellR * 2.15;
    let placed = 0;
    let ring = 0;
    while (placed < n && ring < RING_SIZES.length) {
      const count = Math.min(RING_SIZES[ring], n - placed);
      const radius = ring === 0 ? ringGap * 0.62 : ringGap * (0.62 + ring);
      for (let i = 0; i < count; i++) {
        const a = (i / RING_SIZES[ring]) * Math.PI * 2 + ring * 0.4;
        const idx = placed + i;
        const u = (idx + 0.5) / n; // spread along the outline, alternating sides
        const side = idx % 2 === 0 ? -1 : 1;
        const bx = -FISH_R * 1.0 + u * FISH_R * 2.1;
        const hw = FISH_R * 0.5 * Math.sin(Math.min(1, u * 1.15) * Math.PI) * 0.9 + FISH_R * 0.08;
        const g = new Graphics();
        g.position.set(cx, cy);
        g.scale.set(0);
        content.addChild(g);
        this.cells.push({
          type: types[idx % types.length] ?? 'core', g,
          rx: Math.cos(a) * radius, ry: Math.sin(a) * radius,
          tx: bx, ty: side * hw,
          phase: idx * 1.273,
          order: idx / Math.max(1, n - 1),
        });
      }
      placed += count;
      ring++;
    }

    this.flash = new Graphics();
    this.flash.position.set(cx, cy);
    this.flash.alpha = 0;
    content.addChild(this.flash);

    this.labelTop = new Text({ text: 'EVOLUTION', style: { fill: 0xffd070, fontSize: 14, fontWeight: '700', letterSpacing: 8 } });
    this.labelTop.anchor.set(0.5, 0);
    this.labelTop.position.set(cx, cy - 210);
    this.labelTop.alpha = 0;
    content.addChild(this.labelTop);

    this.title = new Text({ text: 'BECOMING A HUNTER', style: { fill: 0xfff1c8, fontSize: 32, fontWeight: '800', letterSpacing: 4 } });
    this.title.anchor.set(0.5, 0);
    this.title.position.set(cx, cy - 180);
    this.title.alpha = 0;
    content.addChild(this.title);

    this.subtitle = new Text({
      text: 'The colony folds into one body. 518 million years ago, the first fish.\nEverything here is food — or hunting you.',
      style: { fill: 0xffe9c0, fontSize: 14, fontStyle: 'italic', align: 'center', lineHeight: 20 },
    });
    this.subtitle.anchor.set(0.5, 0);
    this.subtitle.position.set(cx, cy + 150);
    this.subtitle.alpha = 0;
    content.addChild(this.subtitle);

    this.prompt = new Text({ text: '▸  CONTINUE', style: { fill: 0xffd070, fontSize: 15, fontWeight: '700', letterSpacing: 5 } });
    this.prompt.anchor.set(0.5, 0);
    this.prompt.position.set(cx, cy + 215);
    this.prompt.alpha = 0;
    content.addChild(this.prompt);
  }

  /** Click / key: skip to the end, or continue once the end is reached. */
  advance(): void {
    if (this.done) return;
    if (!this.ready) { this.t = MAX_T; return; }
    this.done = true;
    this.active = false;
    this.opts.onComplete();
  }

  update(dt: number): void {
    if (!this.active) return;
    if (!this.ready) this.t += dt; else this.holdT += dt;
    const t = this.t + this.holdT;
    this.render(t);
    if (this.t >= MAX_T) {
      this.ready = true;
      const fade = Math.min(1, this.holdT / 0.6);
      this.prompt.alpha = fade * (0.7 + 0.3 * (0.5 + 0.5 * Math.sin(this.holdT * 3)));
    }
  }

  destroy(): void {
    this.active = false;
    this.container.destroy({ children: true });
  }

  private render(t: number): void {
    // Timeline (s):
    //  0.0–1.0  scrim in; cells pop into their rings; "EVOLUTION"
    //  1.0–3.0  colony breathes; title lands
    //  3.0–5.5  cells migrate onto the fish outline, shrinking, warming
    //  5.5–7.0  fusion: cells dissolve, fish fades/scales in, flash ring
    //  7.0–9.0  fish wakes (rocks, breathes); subtitle
    //  ≥9.0     Continue prompt (driven in update)
    this.scrim.alpha = ss(0, 1.0, t) * 0.78;
    this.labelTop.alpha = ss(0.4, 1.6, t);
    this.title.alpha = ss(1.2, 2.6, t) * (1 - ss(5.4, 6.2, t) * 0.35);
    this.subtitle.alpha = ss(7.0, 8.2, t);

    const breathe = 1 + Math.sin(t * 2.2) * 0.04;
    const migrate = ss(3.0, 5.5, t);
    const fuse = ss(5.5, 7.0, t);

    // Envelope: the colony's membrane glow, shrinking as cells leave the rings.
    this.envelope.clear();
    const envR = (this.cellR * 2.15) * (0.62 + Math.min(3, Math.ceil(this.cells.length / 8))) * breathe;
    const envA = ss(0.2, 1.0, t) * (1 - migrate) * 0.16;
    if (envA > 0.005) {
      this.envelope.circle(0, 0, envR * 1.05).fill({ color: 0x4fd1c5, alpha: envA });
      this.envelope.circle(0, 0, envR * 1.05).stroke({ color: 0xe6fffa, width: 1.5, alpha: envA * 2.5 });
    }
    // Warm glow builds under the migration and peaks at fusion.
    this.glow.clear();
    const glowA = migrate * 0.18 + fuse * 0.22 * (1 - ss(7.0, 8.5, t) * 0.6);
    if (glowA > 0.005) {
      this.glow.circle(0, 0, FISH_R * (1.6 + fuse * 0.8) * breathe).fill({ color: 0xffd070, alpha: glowA });
      this.glow.circle(0, 0, FISH_R * (1.0 + fuse * 0.5) * breathe).fill({ color: 0xffe9aa, alpha: glowA * 0.8 });
    }

    // Cells.
    for (const c of this.cells) {
      const pop = ss(0.15 + c.order * 0.5, 0.65 + c.order * 0.5, t);
      // Per-cell migration stagger so the colony peels onto the body in a wave.
      const m = ss(3.0 + c.order * 1.2, 4.3 + c.order * 1.2, t);
      const x = c.rx + (c.tx - c.rx) * m;
      const y = c.ry + (c.ty - c.ry) * m;
      const wob = (1 - m) * Math.sin(t * 2.2 + c.phase) * 2.5;
      c.g.position.set(this.opts.width / 2 + x, this.opts.height / 2 + y + wob);
      const scale = pop * (1 - m * 0.55) * breathe;
      c.g.scale.set(scale);
      c.g.alpha = 1 - fuse;
      if (c.g.alpha <= 0.001) continue;
      const col = mixc(CELL_COLOR[c.type], 0xf9c876, m * 0.8);
      const r = this.cellR;
      c.g.clear();
      c.g.circle(0, 0, r * 1.25).fill({ color: col, alpha: 0.18 });
      c.g.circle(0, 0, r).fill({ color: col, alpha: 0.92 });
      c.g.circle(0, 0, r).stroke({ color: 0xe6fffa, width: 1.2, alpha: 0.7 });
      c.g.circle(-r * 0.3, -r * 0.3, r * 0.38).fill({ color: 0xe6fffa, alpha: 0.35 });
      c.g.circle(0, 0, r * 0.24).fill({ color: 0x0a3a3f, alpha: 0.7 });
      c.g.circle(0, 0, r * 0.14).fill({ color: 0x76e4f7, alpha: 0.75 });
    }

    // Fish: fades in through the fusion, then wakes with a slow rock.
    const wake = ss(7.0, 8.0, t);
    const rock = Math.sin(t * 1.6) * 0.08 * wake;
    this.fish.alpha = fuse;
    this.fishHalo.alpha = fuse;
    const fs = 0.85 + fuse * 0.15;
    this.fish.scale.set(fs);
    this.fishHalo.scale.set(fs);
    this.fish.rotation = rock;
    this.fishHalo.rotation = rock;
    if (fuse > 0.001) {
      this.opts.drawHalo(this.fishHalo, FISH_R, t);
      this.opts.drawFish(this.fish, FISH_R, t);
    }

    // Flash ring at the moment of fusion.
    this.flash.clear();
    const fl = ss(5.5, 5.7, t) * (1 - ss(5.7, 6.6, t));
    if (fl > 0.001) {
      const ring = FISH_R * (1.2 + ss(5.5, 6.6, t) * 3.5);
      this.flash.alpha = 1;
      this.flash.circle(0, 0, ring).stroke({ color: 0xffe9aa, width: 6 * fl + 1, alpha: fl * 0.9 });
      this.flash.circle(0, 0, ring * 0.55).fill({ color: 0xffffff, alpha: fl * 0.25 });
    } else {
      this.flash.alpha = 0;
    }
  }
}
